import React from 'react';
import { Phone, Mail, MapPin, Linkedin, Instagram, Youtube, Facebook } from 'lucide-react';
import { contactData } from '../mock/landingData';
import { useTranslation } from 'react-i18next';

const Contact = () => {
  const { t } = useTranslation();

  // WhatsApp link
  const whatsappLink = `https://wa.me/5517997833002?text=${encodeURIComponent('Olá! Gostaria de falar com um especialista sobre inclusão e acessibilidade.')}`;

  return (
    <section 
      id="contact" 
      className="py-20 bg-gradient-to-b from-white to-gray-50"
      aria-label="Seção de contato"
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span 
            className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold inline-block mb-4"
            role="text"
          >
            {t('contact.badge')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Contact Information */}
          <div className="bg-white rounded-2xl shadow-xl p-10 border border-gray-200">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                {t('contact.heading')}
              </h3>
              <p className="text-gray-600 text-lg">
                {t('contact.description')}
              </p>
            </div>

            {/* Contact Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* WhatsApp */}
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <div className="bg-gradient-to-br from-green-50 to-teal-50 border-2 border-green-200 hover:border-green-400 rounded-xl p-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Phone className="text-white" size={32} />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-xl mb-1">{t('contact.whatsapp')}</p>
                      <p className="text-green-600 font-semibold text-lg">{contactData.phone}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Clique para enviar mensagem via WhatsApp
                  </p>
                </div>
              </a>

              {/* Email */}
              <a
                href={`mailto:${contactData.email}`}
                className="group"
              >
                <div className="bg-gradient-to-br from-blue-50 to-teal-50 border-2 border-blue-200 hover:border-blue-400 rounded-xl p-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Mail className="text-white" size={32} />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-xl mb-1">{t('contact.email')}</p>
                      <p className="text-blue-600 font-semibold text-lg break-all">{contactData.email}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Clique para enviar um e-mail
                  </p>
                </div>
              </a>
            </div>

            {/* Website Info */}
            <div className="flex items-center justify-center space-x-3 p-6 bg-gray-50 rounded-xl mb-8">
              <MapPin className="text-teal-600" size={24} />
              <div>
                <p className="font-semibold text-gray-900">{t('contact.website')}</p>
                <p className="text-gray-600">{contactData.website}</p>
              </div>
            </div>

            {/* Social Links */}
            <div className="text-center">
              <p className="font-bold text-gray-900 mb-6 text-lg">{t('contact.social')}</p>
              <div className="flex justify-center flex-wrap gap-4" role="list" aria-label="Links para redes sociais">
                <a
                  href={contactData.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center hover:scale-110 transition-transform duration-200 shadow-lg"
                  aria-label="Siga-nos no Instagram"
                  role="listitem"
                >
                  <Instagram className="text-white" size={28} />
                </a>
                <a
                  href={contactData.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center hover:scale-110 transition-transform duration-200 shadow-lg"
                  aria-label="Siga-nos no Facebook"
                  role="listitem"
                >
                  <Facebook className="text-white" size={28} />
                </a>
                <a
                  href={`https://linkedin.com/company/${contactData.social.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-14 h-14 bg-blue-700 rounded-xl flex items-center justify-center hover:scale-110 transition-transform duration-200 shadow-lg"
                  aria-label="Conecte-se conosco no LinkedIn"
                  role="listitem"
                >
                  <Linkedin className="text-white" size={28} />
                </a>
                <a
                  href={`https://youtube.com/@${contactData.social.youtube}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center hover:scale-110 transition-transform duration-200 shadow-lg"
                  aria-label="Inscreva-se no nosso canal do YouTube"
                  role="listitem"
                >
                  <Youtube className="text-white" size={28} />
                </a>
              </div>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="mt-10 text-center">
            <div className="bg-gradient-to-r from-teal-600 to-blue-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">
                Pronto para transformar sua organização?
              </h3>
              <p className="text-lg mb-6 opacity-90">
                Entre em contato agora e agende uma conversa com nosso especialista
              </p>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold text-lg transition-colors duration-200"
              >
                📱 Falar no WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
